import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../api/axiosConfig';
import { useSalesScope } from './salesTargetAccess';
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, ComposedChart, PieChart, Pie, Cell
} from 'recharts';
import './SalesAnalysisTab.css';

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];
const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
];

const QUARTERS = [
    { value: 1, label: 'Q1 (Jan-Mar)' },
    { value: 2, label: 'Q2 (Apr-Jun)' },
    { value: 3, label: 'Q3 (Jul-Sep)' },
    { value: 4, label: 'Q4 (Oct-Dec)' }
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const formatNum = (n) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n ?? 0);

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        const remaining = data.remaining;
        const isExceeded = remaining < 0;
        
        return (
            <div style={{
                backgroundColor: 'white',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
                <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>Day: {label}</p>
                {payload.map((entry, index) => (
                    <p key={index} style={{ margin: '4px 0', color: entry.color }}>
                        {entry.name}: {formatNum(entry.value)}
                    </p>
                ))}
                <p style={{ 
                    margin: '8px 0 0 0', 
                    fontWeight: 'bold',
                    color: isExceeded ? '#22c55e' : '#ef4444'
                }}>
                    {isExceeded 
                        ? `Target Exceeded by ${formatNum(Math.abs(remaining))} ✓`
                        : `Remaining: ${formatNum(remaining)} to reach target`
                    }
                </p>
            </div>
        );
    }
    return null;
};

const IconRefresh = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="23 4 23 10 17 10"/>
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
    </svg>
);

const SalesAnalysisTab = () => {
    const { user } = useAuth();
    // Where this user's view starts and whether they see the whole group — from
    // the server, never guessed from the role (see useSalesScope). null = loading.
    const scope = useSalesScope(user);
    const restricted = scope ? !scope.unrestricted : false;
    const noBranches = scope?.home?.level === 'none';
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Organization data
    const [rootOrgs, setRootOrgs] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [branches, setBranches] = useState([]);
    const [salesmen, setSalesmen] = useState([]);
    
    // Salesman filters (for filtering the salesman dropdown)
    const [salesmanFilters, setSalesmanFilters] = useState({
        companyId: '',
        branchId: ''
    });
    
    // Filters
    const [filters, setFilters] = useState({
        level: 'root', // 'root', 'company', 'branch', 'salesman' — replaced by the scope's home on load
        organizationId: '',
        salesmanId: '',
        periodType: 'month', // 'month', 'months', 'quarter', 'yearly'
        year: CURRENT_YEAR,
        month: new Date().getMonth() + 1,
        selectedMonths: [],
        quarter: 1
    });
    
    // Analytics data
    const [trendingData, setTrendingData] = useState(null);
    const [breakdownData, setBreakdownData] = useState(null);

    useEffect(() => {
        fetchOrganizations();
        fetchSalesmen();
    }, []);

    // Refetch salesmen when salesman filters change
    useEffect(() => {
        if (filters.level === 'salesman') {
            fetchSalesmen();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [salesmanFilters.companyId, salesmanFilters.branchId]);

    // Open on the scope's home: a branch user on their branch, a user covering a
    // whole company on that company, everyone else on the group (which the
    // server trims to their own branches and measures against their targets).
    useEffect(() => {
        if (!scope || noBranches) return;
        const home = scope.home || {};
        if (home.level === 'root') return;
        setFilters(prev => ({
            ...prev,
            level: home.level,
            organizationId: home.organizationId || ''
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope]);

    useEffect(() => {
        // Wait for the scope, so the first request is for the right organization
        // rather than the group view followed by a second fetch.
        if (!scope || noBranches) return;
        const ready = filters.level === 'root'
            || (filters.level === 'salesman' ? !!filters.salesmanId : !!filters.organizationId);
        if (ready) fetchAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, scope]);

    const fetchOrganizations = async () => {
        try {
            const res = await axiosInstance.get('/api/sales-target/organizations/tree');
            if (res.data.success) {
                const extractByType = (nodes, type) => {
                    let list = [];
                    nodes.forEach(n => {
                        if (n.type === type) list.push(n);
                        if (n.children) list = [...list, ...extractByType(n.children, type)];
                    });
                    return list;
                };
                
                setRootOrgs(res.data.data.filter(n => n.type === 'root'));
                setCompanies(extractByType(res.data.data, 'company'));
                setBranches(extractByType(res.data.data, 'branch'));
            }
        } catch (err) {
            console.error('Failed to fetch organizations:', err);
        }
    };

    const fetchSalesmen = async () => {
        try {
            const params = {};
            
            // The server already limits the list to the caller's branches; these
            // only narrow it further.
            if (salesmanFilters.branchId) {
                params.branchId = salesmanFilters.branchId;
            } else if (salesmanFilters.companyId) {
                params.companyId = salesmanFilters.companyId;
            }
            
            const res = await axiosInstance.get('/api/sales-target/salesmen', { params });
            if (res.data.success) {
                setSalesmen(res.data.data);
            }
        } catch (err) {
            console.error('Failed to fetch salesmen:', err);
        }
    };

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            setError(null);

            const params = {
                year: filters.year
            };

            // Add period filter
            if (filters.periodType === 'month') {
                params.month = filters.month;
            } else if (filters.periodType === 'months' && filters.selectedMonths.length > 0) {
                params.months = filters.selectedMonths.join(',');
            } else if (filters.periodType === 'quarter') {
                params.quarter = filters.quarter;
            } else if (filters.periodType === 'yearly') {
                params.yearly = 'true';
            }

            // Check if salesman-wise analysis
            if (filters.level === 'salesman' && filters.salesmanId) {
                // Fetch salesman-specific trending data
                params.salesmanId = filters.salesmanId;
                const trendingRes = await axiosInstance.get('/api/sales-target/analytics/salesman-trending', { params });
                if (trendingRes.data.success) {
                    setTrendingData(trendingRes.data.data);
                }

                // Fetch breakdown data for salesman
                const breakdownRes = await axiosInstance.get('/api/sales-target/analytics/breakdown', { params });
                if (breakdownRes.data.success) {
                    setBreakdownData(breakdownRes.data.data);
                }
            } else {
                // Organization-level analysis
                if (filters.level !== 'root' && filters.organizationId) {
                    params.organizationId = filters.organizationId;
                }

                // Fetch trending data
                const trendingRes = await axiosInstance.get('/api/sales-target/analytics/trending', { params });
                if (trendingRes.data.success) {
                    setTrendingData(trendingRes.data.data);
                }

                // Fetch breakdown data
                const breakdownRes = await axiosInstance.get('/api/sales-target/analytics/breakdown', { params });
                if (breakdownRes.data.success) {
                    setBreakdownData(breakdownRes.data.data);
                }
            }

        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load analytics');
            console.error('Fetch analytics error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleExportToExcel = async () => {
        try {
            const params = {
                year: filters.year
            };

            // Add period filter
            if (filters.periodType === 'month') {
                params.month = filters.month;
            } else if (filters.periodType === 'months' && filters.selectedMonths.length > 0) {
                params.months = filters.selectedMonths.join(',');
            } else if (filters.periodType === 'quarter') {
                params.quarter = filters.quarter;
            } else if (filters.periodType === 'yearly') {
                params.yearly = 'true';
            }

            // Add organization or salesman filter
            if (filters.level === 'salesman' && filters.salesmanId) {
                params.salesmanId = filters.salesmanId;
            } else if (filters.level !== 'root' && filters.organizationId) {
                params.organizationId = filters.organizationId;
            }

            // Make request to export endpoint
            const response = await axiosInstance.get('/api/sales-target/analytics/export-excel', {
                params,
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            
            // Extract filename from Content-Disposition header or use default
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'Sales_Analysis.xlsx';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (err) {
            console.error('Export to Excel error:', err);
            alert('Failed to export to Excel. Please try again.');
        }
    };

    const handleExportToPdf = async () => {
        try {
            const params = {
                year: filters.year
            };

            if (filters.periodType === 'month') {
                params.month = filters.month;
            } else if (filters.periodType === 'months' && filters.selectedMonths.length > 0) {
                params.months = filters.selectedMonths.join(',');
            } else if (filters.periodType === 'quarter') {
                params.quarter = filters.quarter;
            } else if (filters.periodType === 'yearly') {
                params.yearly = 'true';
            }

            if (filters.level === 'salesman' && filters.salesmanId) {
                params.salesmanId = filters.salesmanId;
            } else if (filters.level !== 'root' && filters.organizationId) {
                params.organizationId = filters.organizationId;
            }

            const response = await axiosInstance.get('/api/sales-target/analytics/export-pdf', {
                params,
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'Sales_Analysis.pdf';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (err) {
            console.error('Export to PDF error:', err);
            alert('Failed to export to PDF. Please try again.');
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => {
            const newFilters = { ...prev, [key]: value };
            
            // Reset dependent filters
            if (key === 'level') {
                newFilters.organizationId = '';
                newFilters.salesmanId = '';
                // Reset salesman filters when changing level
                setSalesmanFilters({ companyId: '', branchId: '' });
            }
            
            return newFilters;
        });
    };

    const handleSalesmanFilterChange = (key, value) => {
        setSalesmanFilters(prev => {
            const newFilters = { ...prev, [key]: value };
            // Reset branch when company changes
            if (key === 'companyId') {
                newFilters.branchId = '';
            }
            return newFilters;
        });
    };

    const toggleMonth = (month) => {
        setFilters(prev => {
            const selected = prev.selectedMonths.includes(month)
                ? prev.selectedMonths.filter(m => m !== month)
                : [...prev.selectedMonths, month].sort((a, b) => a - b);
            return { ...prev, selectedMonths: selected };
        });
    };

    const getOrganizationOptions = () => {
        if (filters.level === 'root') return rootOrgs;
        if (filters.level === 'company') return companies;
        if (filters.level === 'branch') return branches;
        return [];
    };

    // Get filtered branches based on selected company in salesman filters
    const getFilteredBranches = () => {
        if (!salesmanFilters.companyId) {
            return branches; // Show all branches if no company selected
        }
        // Filter branches that belong to the selected company
        return branches.filter(branch => branch.parentId === salesmanFilters.companyId);
    };

    // Prepare chart data
    const chartData = trendingData?.dailyData?.map(item => {
        const remaining = item.cumulativeTarget - item.cumulativeSales;
        return {
            label: `${item.day}/${item.month}`,
            day: item.day,
            month: item.month,
            dailySales: item.dailySales,
            cumulativeSales: item.cumulativeSales,
            cumulativeTarget: item.cumulativeTarget,
            dailyTarget: item.dailyTarget,
            remaining: remaining
        };
    }) || [];

    const pieData = breakdownData?.breakdown?.filter(item => item.amount > 0).map(item => ({
        name: item.type,
        value: item.amount,
        percentage: item.percentage
    })) || [];

    // Custom tooltip for the chart
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            const remaining = data.remaining;
            const isExceeded = remaining < 0;
            
            return (
                <div style={{
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '10px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>Day: {label}</p>
                    <p style={{ margin: '4px 0', color: '#8884d8' }}>
                        Daily Sales: {formatNum(data.dailySales)}
                    </p>
                    <p style={{ margin: '4px 0', color: '#82ca9d' }}>
                        Cumulative Sales: {formatNum(data.cumulativeSales)}
                    </p>
                    <p style={{ margin: '4px 0', color: '#ff7300' }}>
                        Cumulative Target: {formatNum(data.cumulativeTarget)}
                    </p>
                    <p style={{ 
                        margin: '8px 0 0 0', 
                        fontWeight: 'bold',
                        color: isExceeded ? '#52c41a' : '#ff4d4f',
                        borderTop: '1px solid #eee',
                        paddingTop: '8px'
                    }}>
                        {isExceeded 
                            ? `✓ Target Exceeded by ${formatNum(Math.abs(remaining))}`
                            : `Remaining: ${formatNum(remaining)}`
                        }
                    </p>
                </div>
            );
        }
        return null;
    };

    if (loading && !trendingData) {
        return (
            <div className="sales-analysis-tab">
                <div className="analysis-loading">
                    <div className="spinner" />
                    <p>Loading analytics...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="sales-analysis-tab">
            {/* Header */}
            <div className="analysis-header">
                <h2>Sales Analysis</h2>
                <div className="header-actions">
                    <button 
                        className="btn-export" 
                        onClick={handleExportToExcel} 
                        disabled={loading || !trendingData}
                        title="Export to Excel"
                    >
                        📊 Export to Excel
                    </button>
                    <button 
                        className="btn-export" 
                        onClick={handleExportToPdf} 
                        disabled={loading || !trendingData}
                        title="Export to PDF"
                        style={{ marginLeft: '10px', backgroundColor: '#e74c3c' }}
                    >
                        📄 Export to PDF
                    </button>
                    <button className="btn-refresh" onClick={fetchAnalytics} disabled={loading}>
                        <IconRefresh /> Refresh
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="analysis-filters">
                {/* Row 1: Organization Level and Selection */}
                <div className="filter-row">
                    <div className="filter-group full-width">
                        <label>Organization Level</label>
                        <div className="radio-group">
                            {/* Every level is safe to offer: the organization lists come
                                from the scoped tree, and the server trims any view to the
                                user's branches. For a restricted user "root" is the total
                                of their own branches, so it is labelled as such. */}
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    value="root"
                                    checked={filters.level === 'root'}
                                    onChange={(e) => handleFilterChange('level', e.target.value)}
                                />
                                <span>{restricted ? 'All My Branches' : 'Total Root'}</span>
                            </label>
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    value="company"
                                    checked={filters.level === 'company'}
                                    onChange={(e) => handleFilterChange('level', e.target.value)}
                                />
                                <span>Company-wise</span>
                            </label>
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    value="branch"
                                    checked={filters.level === 'branch'}
                                    onChange={(e) => handleFilterChange('level', e.target.value)}
                                />
                                <span>Branch-wise</span>
                            </label>
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    value="salesman"
                                    checked={filters.level === 'salesman'}
                                    onChange={(e) => handleFilterChange('level', e.target.value)}
                                />
                                <span>Salesman-wise</span>
                            </label>
                        </div>
                    </div>

                    {/* Organization Selector */}
                    {filters.level === 'company' && (
                        <div className="filter-group">
                            <label>Select Company</label>
                            <select
                                value={filters.organizationId}
                                onChange={(e) => handleFilterChange('organizationId', e.target.value)}
                            >
                                <option value="">-- Select company --</option>
                                {companies.map(org => (
                                    <option key={org._id} value={org._id}>{org.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {filters.level === 'branch' && (
                        <div className="filter-group">
                            <label>Select Branch</label>
                            <select
                                value={filters.organizationId}
                                onChange={(e) => handleFilterChange('organizationId', e.target.value)}
                            >
                                <option value="">-- Select branch --</option>
                                {branches.map(org => (
                                    <option key={org._id} value={org._id}>{org.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {filters.level === 'salesman' && (
                        <>
                            {/* Optional narrowing — the lists are already the user's own */}
                            {(
                                <>
                                    <div className="filter-group">
                                        <label>Filter by Company (Optional)</label>
                                        <select
                                            value={salesmanFilters.companyId}
                                            onChange={(e) => handleSalesmanFilterChange('companyId', e.target.value)}
                                        >
                                            <option value="">-- All Companies --</option>
                                            {companies.map(org => (
                                                <option key={org._id} value={org._id}>{org.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    
                                    <div className="filter-group">
                                        <label>Filter by Branch (Optional)</label>
                                        <select
                                            value={salesmanFilters.branchId}
                                            onChange={(e) => handleSalesmanFilterChange('branchId', e.target.value)}
                                        >
                                            <option value="">-- All Branches --</option>
                                            {getFilteredBranches().map(org => (
                                                <option key={org._id} value={org._id}>{org.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}
                            
                            <div className="filter-group">
                                <label>Select Salesman</label>
                                <select
                                    value={filters.salesmanId}
                                    onChange={(e) => handleFilterChange('salesmanId', e.target.value)}
                                >
                                    <option value="">-- Select salesman --</option>
                                    {salesmen.map(s => (
                                        <option key={s._id} value={s._id}>
                                            {s.name} ({s.employeeId}) - {s.branchName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}
                </div>

                {/* Row 2: Period Selection */}
                <div className="filter-row">
                    <div className="filter-group">
                        <label>Year</label>
                        <select value={filters.year} onChange={(e) => handleFilterChange('year', parseInt(e.target.value))}>
                            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Period Type</label>
                        <select value={filters.periodType} onChange={(e) => handleFilterChange('periodType', e.target.value)}>
                            <option value="month">Single Month</option>
                            <option value="months">Multiple Months</option>
                            <option value="quarter">Quarter</option>
                            <option value="yearly">Yearly (All 12 Months)</option>
                        </select>
                    </div>

                    {/* Period Selector */}
                    {filters.periodType === 'month' && (
                        <div className="filter-group">
                            <label>Month</label>
                            <select value={filters.month} onChange={(e) => handleFilterChange('month', parseInt(e.target.value))}>
                                {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                        </div>
                    )}

                    {filters.periodType === 'quarter' && (
                        <div className="filter-group">
                            <label>Quarter</label>
                            <select value={filters.quarter} onChange={(e) => handleFilterChange('quarter', parseInt(e.target.value))}>
                                {QUARTERS.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
                            </select>
                        </div>
                    )}

                    {filters.periodType === 'yearly' && (
                        <div className="filter-group">
                            <label>View</label>
                            <div className="yearly-indicator">
                                <span className="indicator-badge">📊 Viewing All 12 Months</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Multiple Months Selector */}
            {filters.periodType === 'months' && (
                <div className="months-selector">
                    <label>Select Months:</label>
                    <div className="month-checkboxes">
                        {MONTHS.map(m => (
                            <label key={m.value} className="month-checkbox">
                                <input
                                    type="checkbox"
                                    checked={filters.selectedMonths.includes(m.value)}
                                    onChange={() => toggleMonth(m.value)}
                                />
                                <span>{m.label.substring(0, 3)}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {error && <div className="error-message">⚠ {error}</div>}

            {noBranches && (
                <div className="info-message" style={{
                    padding: '20px',
                    margin: '20px 0',
                    backgroundColor: '#fff7ed',
                    border: '1px solid #f59e0b',
                    borderRadius: '8px',
                    textAlign: 'center',
                    color: '#b45309'
                }}>
                    <h3 style={{ margin: '10px 0' }}>No branches are assigned to you</h3>
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        Ask an administrator to set your company and branch in User Management.
                    </p>
                </div>
            )}

            {/* Message when a level needs a selection before anything can load */}
            {!noBranches && !trendingData && !loading && filters.level !== 'root'
                && !(filters.level === 'salesman' ? filters.salesmanId : filters.organizationId) && (
                <div className="info-message" style={{
                    padding: '20px',
                    margin: '20px 0',
                    backgroundColor: '#e3f2fd',
                    border: '1px solid #2196f3',
                    borderRadius: '8px',
                    textAlign: 'center',
                    color: '#1976d2'
                }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>📊</div>
                    <h3 style={{ margin: '10px 0', color: '#1565c0' }}>Please Select a Branch or Salesman</h3>
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        Choose a branch or salesman from the filters above to view sales analysis data.
                    </p>
                </div>
            )}

            {/* Yearly View Banner */}
            {trendingData && trendingData.period.isYearly && (
                <div className="yearly-view-banner">
                    <div className="banner-content">
                        <span className="banner-icon">📊</span>
                        <div className="banner-text">
                            <strong>Yearly Analysis Mode</strong>
                            <span>Viewing day-by-day performance for all 12 months of {trendingData.period.year} ({trendingData.dailyData.length} days)</span>
                        </div>
                    </div>
                </div>
            )}

            {trendingData && (
                <>
                    {/* Summary Cards */}
                    <div className="analysis-summary-cards">
                        <div className="summary-card">
                            <div className="card-icon">🎯</div>
                            <span className="card-label">Total Target</span>
                            <span className="card-value">{formatNum(trendingData.summary.totalTarget)}</span>
                            <span className="card-subtitle">
                                {trendingData.period.isYearly ? 'Annual Target' : `${trendingData.period.monthNames?.join(', ')}`}
                            </span>
                        </div>
                        <div className="summary-card">
                            <div className="card-icon">💰</div>
                            <span className="card-label">Total Achieved</span>
                            <span className="card-value achieved">{formatNum(trendingData.summary.totalAchieved)}</span>
                            <span className="card-subtitle">
                                {trendingData.period.isYearly ? 'Year to Date' : 'Period Total'}
                            </span>
                        </div>
                        <div className="summary-card">
                            <div className="card-icon">📊</div>
                            <span className="card-label">Achievement</span>
                            <span className={`card-value ${trendingData.summary.completionPercentage >= 100 ? 'success' : trendingData.summary.completionPercentage >= 80 ? 'warning' : 'danger'}`}>
                                {trendingData.summary.completionPercentage.toFixed(2)}%
                            </span>
                            <span className="card-subtitle">
                                {trendingData.summary.completionPercentage >= 100 ? '🎉 Target Exceeded!' : 
                                 trendingData.summary.completionPercentage >= 80 ? '⚡ On Track' : '⚠️ Below Target'}
                            </span>
                        </div>
                        <div className="summary-card">
                            <div className="card-icon">📅</div>
                            <span className="card-label">Period</span>
                            <span className="card-value small">
                                {trendingData.period.isYearly ? 'Full Year' : trendingData.period.monthNames?.join(', ')}
                            </span>
                            <span className="card-subtitle">
                                {trendingData.period.year} • {trendingData.dailyData.length} {trendingData.period.isYearly ? 'Months' : 'Days'}
                            </span>
                        </div>
                    </div>

                    {/* Main Chart - Trending */}
                    <div className="chart-container main-chart">
                        <h3>
                            {filters.level === 'salesman' && trendingData.salesman
                                ? `${trendingData.salesman.name} - Sales Trending`
                                : trendingData.period.isYearly 
                                    ? 'Sales Trending - Full Year (Day by Day)' 
                                    : 'Sales Trending - Day by Day'}
                        </h3>
                        <ResponsiveContainer width="100%" height={400}>
                            <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    dataKey="label" 
                                    angle={-45}
                                    textAnchor="end"
                                    height={80}
                                    interval={trendingData.period.isYearly ? Math.floor(chartData.length / 30) : Math.floor(chartData.length / 20)}
                                />
                                <YAxis />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Bar 
                                    dataKey="dailySales" 
                                    fill="#8884d8" 
                                    name="Daily Sales"
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="cumulativeSales" 
                                    stroke="#82ca9d" 
                                    strokeWidth={3}
                                    name="Cumulative Sales"
                                    dot={false}
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="cumulativeTarget" 
                                    stroke="#ff7300" 
                                    strokeWidth={2}
                                    strokeDasharray="5 5"
                                    name="Cumulative Target"
                                    dot={false}
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Secondary Charts Row */}
                    <div className="charts-row">
                        {/* Sales Breakdown */}
                        {breakdownData && pieData.length > 0 && (
                            <div className="chart-container">
                                <h3>Sales Breakdown by Type</h3>
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={({ name, percentage }) => `${name}: ${percentage}%`}
                                            outerRadius={80}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {pieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value) => formatNum(value)} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="breakdown-legend">
                                    {pieData.map((item, index) => (
                                        <div key={item.name} className="legend-item">
                                            <span className="legend-color" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>
                                            <span className="legend-text">{item.name}: {formatNum(item.value)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Daily Target Info */}
                        <div className="chart-container info-panel">
                            <h3>Period Information</h3>
                            <div className="info-content">
                                {filters.level === 'salesman' && trendingData.salesman ? (
                                    <>
                                        <div className="info-item">
                                            <span className="info-label">Salesman:</span>
                                            <span className="info-value">{trendingData.salesman.name}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Employee ID:</span>
                                            <span className="info-value">{trendingData.salesman.employeeId}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Branch:</span>
                                            <span className="info-value">{trendingData.salesman.branchName}</span>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="info-item">
                                            <span className="info-label">Organization:</span>
                                            <span className="info-value">{trendingData.organization?.name || 'N/A'}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Type:</span>
                                            <span className="info-value">{trendingData.organization?.type || 'N/A'}</span>
                                        </div>
                                    </>
                                )}
                                <div className="info-item">
                                    <span className="info-label">Period:</span>
                                    <span className="info-value">
                                        {trendingData.period.monthNames?.join(', ')} {trendingData.period.year}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Total Days:</span>
                                    <span className="info-value">{trendingData.dailyData.length}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Remaining:</span>
                                    <span className={`info-value ${trendingData.summary.totalAchieved >= trendingData.summary.totalTarget ? 'success' : 'danger'}`}>
                                        {trendingData.summary.totalAchieved >= trendingData.summary.totalTarget
                                            ? `Target Exceeded by ${formatNum(trendingData.summary.totalAchieved - trendingData.summary.totalTarget)}`
                                            : `${formatNum(trendingData.summary.totalTarget - trendingData.summary.totalAchieved)} to reach target`
                                        }
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default SalesAnalysisTab;
